import {
  useBillingStore,
  type BillingTermKind,
  type CatalogAddon,
  type CatalogOffering,
  type CatalogTemplate,
  type SimulationInput,
  type SimulationQuote,
} from '~/store/billing'
import type { CheckoutDatacenter } from '~/composables/useDatacenterDisplay'

/**
 * Everything about CONFIGURING and PRICING a server, with nothing about who is
 * buying it.
 *
 * There are two surfaces: `/configure`, which anyone can reach and which exists
 * so a visitor can see a price before we ask them for anything, and
 * `/billing/checkout`, which additionally offers what only an account can have
 * — an existing subscription to bill against, a private network, your own
 * images, a coupon, your balance. Those are genuinely different pages, and the
 * public one must not be the private one with ten `v-if="signedIn"` through it.
 *
 * What they must NOT be is two implementations of the same wizard. This repo
 * has paid for that more than once — seven cells that answered "whose row is
 * this", two writers of the blank-instance template, a reinstall OS list that
 * drifted from the checkout's until one `TemplatePicker` replaced both. So the
 * configuration lives here, once, and each page supplies what it alone knows.
 *
 * The boundary is simple: anything the SIMULATOR needs is in here, because the
 * simulator is public and takes no account context. Anything about money that
 * has already changed hands — balances, coupons, invoices, orders — is not.
 */

export type ClientImageOption = {
  id: string
  name: string
  minDiskGb: number | null
  hasCloudInit: boolean
  /** The customer wrote their own first-boot configuration for this image. */
  hasCustomCloudInit: boolean
  sizeLabel: string
}

/** One automatic change the configurator made on the customer's behalf. */
export type AutoAdjustment = {
  key: string
  /** Ready-to-render sentence. Same string for every reader, sighted or not. */
  label: string
}

/**
 * Sources are READ, never written, so a `computed` is as good as a `ref` here —
 * and the public page's location list is a computed, because it has to come out
 * of the SSR payload rather than out of a ref the handler assigned into.
 */
type ReadonlySource<T> = Ref<T> | ComputedRef<T>

export type ConfiguratorSources = {
  /** Locations to offer. Public list on `/configure`, same shape either way. */
  datacenters: ReadonlySource<CheckoutDatacenter[]>
  /**
   * The customer's own ready disk images. Always empty on the public page —
   * the "my images" tab is one of the things only an account can have.
   */
  clientImages?: ReadonlySource<ClientImageOption[]>
  /** VPCs for private connectivity. Empty on the public page, same reason. */
  vpcs?: ReadonlySource<any[]>
  /**
   * Forces the term, overriding the customer's cycle choice.
   *
   * The signed-in checkout uses it when the order is being added to an EXISTING
   * subscription: that subscription's cycle is a fact about it, not a choice
   * being made here. Null means the customer's own selection stands.
   */
  termOverride?: Ref<BillingTermKind | null>
}

/**
 * Hard product ceiling of public addresses per instance — mirrors the API's
 * MAX_IPV4_PER_INSTANCE / MAX_IPV6_PER_INSTANCE, which refuses above it.
 */
const MAX_IPS_PER_INSTANCE = 24

export function useCheckoutConfigurator(sources: ConfiguratorSources) {
  const billing = useBillingStore()
  const pricing = useCheckoutPricing()
  const { t } = useI18n()
  const { $mwApi } = useNuxtApp()

  const datacenters = sources.datacenters
  const clientImages = sources.clientImages ?? ref<ClientImageOption[]>([])
  const vpcs = sources.vpcs ?? ref<any[]>([])
  const termOverride = sources.termOverride ?? ref<BillingTermKind | null>(null)

  // ── Selection state ─────────────────────────────────────────────────────
  const selectedDatacenterId = ref<number | null>(null)
  const selectedOfferingCode = ref<string | null>(null)
  const selectedTerm = ref<BillingTermKind>('MONTHLY')
  const osTab = ref<'os' | 'marketplace' | 'byo'>('os')
  /**
   * Which of the customer's own images the "my images" tab has selected.
   *
   * `null` means the blank-instance option — which is what this tab used to be
   * in its entirety, so null has to stay a legitimate, explicit choice rather
   * than "nothing picked yet".
   */
  const selectedClientImageId = ref<string | null>(null)
  // Keep each source's last explicit choice. Customers can compare a clean OS
  // with a marketplace image without losing their previous selection, and merely
  // opening the marketplace never silently adds an app to their order.
  const selectedOsTemplateCode = ref<string | null>(null)
  const selectedMarketplaceTemplateCode = ref<string | null>(null)
  const selectedTemplateCode = computed<string | null>({
    get: () => {
      if (osTab.value === 'os') return selectedOsTemplateCode.value
      if (osTab.value === 'marketplace') return selectedMarketplaceTemplateCode.value
      return null
    },
    set: (value) => {
      if (osTab.value === 'os') selectedOsTemplateCode.value = value
      if (osTab.value === 'marketplace') selectedMarketplaceTemplateCode.value = value
    },
  })
  const connectivityMode = ref<'public' | 'private'>('public')
  const selectedVpcId = ref<number | null>(null)
  const mainDiskExtraGb = ref(0)
  const extraDisks = ref<{ size_gb: number }[]>([])
  const addonQuantities = ref<Record<string, number>>({})

  /** The term everything is actually priced at. */
  const checkoutTerm = computed<BillingTermKind>(
    () => termOverride.value ?? selectedTerm.value
  )

  // ── Locations ───────────────────────────────────────────────────────────
  /**
   * `deployable` comes from the API: false means no hypervisor in that location
   * can currently receive an instance (all drained, out of service, saturated or
   * unwired). The order endpoint refuses those, so the picker must not let one be
   * selected — otherwise the customer configures a whole instance and is rejected
   * at the last click.
   */
  const deployableDatacenters = computed(() =>
    datacenters.value.filter((d: any) => d.deployable !== false)
  )

  const selectedDatacenter = computed(
    () => datacenters.value.find((d) => d.id === selectedDatacenterId.value) ?? null
  )

  /**
   * Whether the selected location can allocate each IP type — booleans only,
   * the API deliberately never exposes inventory counts.
   *
   * Read from the location list when it carries the fields (the public
   * endpoint answers both in one call) and only otherwise round-tripped. An
   * unknown answer FAILS OPEN: not knowing must not remove an option.
   */
  const ipAvailability = ref<{ ipv4: boolean; ipv6: boolean } | null>(null)
  watch(
    [selectedDatacenterId, datacenters],
    async ([dcId]) => {
      ipAvailability.value = null
      if (!dcId) return

      const fromList = datacenters.value.find((d: any) => d.id === dcId) as any
      if (fromList && typeof fromList.ipv4_available === 'boolean') {
        ipAvailability.value = {
          ipv4: fromList.ipv4_available,
          ipv6: fromList.ipv6_available === true,
        }
        return
      }

      try {
        const res = await ($mwApi as any).billingCatalogIpAvailability(dcId)
        ipAvailability.value = {
          ipv4: res.data.ipv4_available,
          ipv6: res.data.ipv6_available,
        }
      } catch {
        ipAvailability.value = null // unknown → don't block (fail open)
      }
    },
    { immediate: true }
  )

  const vpcsInDatacenter = computed(() =>
    vpcs.value.filter(
      (v: any) =>
        !selectedDatacenterId.value ||
        v.datacenter_id == null ||
        Number(v.datacenter_id) === selectedDatacenterId.value
    )
  )

  const selectedVpcName = computed(
    () => vpcs.value.find((v: any) => v.id === selectedVpcId.value)?.name ?? ''
  )

  // ── Plans and storage ───────────────────────────────────────────────────
  // Compute plans only; VPN gateways have their own checkout.
  const computeOfferings = computed(
    () =>
      billing.catalog?.offerings.filter((o) => o.offering_kind === 'COMPUTE') ?? []
  )
  const selectedOffering = computed(
    () =>
      computeOfferings.value.find((o) => o.code === selectedOfferingCode.value) ??
      null
  )
  const baseDiskGb = computed(() => selectedOffering.value?.main_disk_base_gb ?? 30)
  // Storage granularity comes from the DB (disk addon quantity_step), not a
  // hardcoded block size. All disk controls snap to this step.
  const diskStep = computed(() => billing.catalog?.storage?.step_gb ?? 10)
  const minExtraDiskGb = computed(
    () => billing.catalog?.storage?.min_extra_gb ?? diskStep.value
  )
  const mainDiskTotalGb = computed(() => baseDiskGb.value + mainDiskExtraGb.value)
  const totalStorageGb = computed(
    () =>
      mainDiskTotalGb.value +
      extraDisks.value.reduce((sum, d) => sum + d.size_gb, 0)
  )

  // ── OS choice ───────────────────────────────────────────────────────────
  const selectedTemplate = computed<CatalogTemplate | null>(
    () =>
      billing.catalog?.templates.find(
        (tmpl) => tmpl.code === selectedTemplateCode.value
      ) ?? null
  )
  const selectedClientImage = computed(
    () => clientImages.value.find((i) => i.id === selectedClientImageId.value) ?? null
  )
  const osChoice = computed(() =>
    osTab.value === 'byo'
      ? selectedClientImageId.value
        ? {
            type: 'client_image' as const,
            client_image_id: selectedClientImageId.value,
          }
        : { type: 'byo_iso' as const }
      : selectedTemplateCode.value
        ? { type: 'template' as const, template_code: selectedTemplateCode.value }
        : null
  )
  /**
   * What the chosen OS is CALLED, whichever kind of source it came from — a
   * template, one of the customer's images, or the blank instance, which is an
   * explicit choice and must not read as an empty line. The ONE name every
   * surface prints: the order summary beside the page and the commit sheet used
   * to disagree, the summary dropping a customer image's name entirely.
   */
  const osDisplayName = computed<string | null>(
    () =>
      selectedTemplate.value?.name ??
      selectedClientImage.value?.name ??
      (osChoice.value?.type === 'byo_iso' ? t('billing.page.checkout.byo.title') : null)
  )

  // ── OS minimum requirements ─────────────────────────────────────────────
  // The OS installs on the MAIN disk, so min_disk_gb is checked against the main
  // disk total (never the extra data disks). RAM/vCPU come from the plan.
  // A customer image states its own minimum, in exactly the same terms a template
  // does — so the auto-raise, the blockers and the storage step all keep working
  // without knowing which kind of source was chosen.
  const osMinDiskGb = computed(
    () => selectedClientImage.value?.minDiskGb ?? selectedTemplate.value?.min_disk_gb ?? null
  )
  const osMinRamMb = computed(() => selectedTemplate.value?.min_ram_mb ?? null)
  const osMinVcpu = computed(() => selectedTemplate.value?.min_vcpu ?? null)

  /** Minimum main-disk EXPANSION (GB, multiple of step) to satisfy the OS disk
   * minimum on the current plan. The disk - button cannot go below this. */
  const minMainExtraGb = computed(() => {
    if (osMinDiskGb.value == null) return 0
    const needed = osMinDiskGb.value - baseDiskGb.value
    if (needed <= 0) return 0
    return Math.ceil(needed / diskStep.value) * diskStep.value
  })

  const diskBelowOsMin = computed(
    () => osMinDiskGb.value != null && mainDiskTotalGb.value < osMinDiskGb.value
  )
  const ramBelowOsMin = computed(
    () =>
      osMinRamMb.value != null &&
      !!selectedOffering.value &&
      selectedOffering.value.ram_mb < osMinRamMb.value
  )
  const cpuBelowOsMin = computed(
    () =>
      osMinVcpu.value != null &&
      !!selectedOffering.value &&
      selectedOffering.value.vcpu < osMinVcpu.value
  )
  /** RAM/vCPU are plan-bound — the user must pick a bigger plan to satisfy them. */
  const planBelowOsMin = computed(() => ramBelowOsMin.value || cpuBelowOsMin.value)
  const osRequirementsUnmet = computed(
    () => diskBelowOsMin.value || planBelowOsMin.value
  )

  /** Whether a given plan can host the currently-selected OS (RAM/vCPU only;
   * disk is adjustable). Used to disable incompatible plan cards. */
  function offeringMeetsOsMin(o: CatalogOffering): boolean {
    if (osMinRamMb.value != null && o.ram_mb < osMinRamMb.value) return false
    if (osMinVcpu.value != null && o.vcpu < osMinVcpu.value) return false
    return true
  }

  // ── Automatic changes, and saying so ────────────────────────────────────
  /**
   * Changes the configurator made to the customer's configuration by itself.
   *
   * The OS chooses the floor for the main disk, and until now raising it was
   * silent: the disk grew and the price moved with it, with the only
   * explanation rendered in a step ABOVE where the customer was looking — and
   * not rendered at all for a customer image, whose name the note needed and
   * never got.
   *
   * Choosing the OS before the storage step (which is why they are in that
   * order) means this normally has nothing to report: the slider simply opens
   * at the floor. What is left is the case that cannot be designed away —
   * changing the OS, or the plan, AFTER sizing the disk — and that is what this
   * list is for. Rendered where the change happened and in the order summary,
   * never as a toast: this moves the price, and a toast is missable.
   */
  const raisedDiskFrom = ref<number | null>(null)
  const autoAdjustments = computed<AutoAdjustment[]>(() => {
    const items: AutoAdjustment[] = []
    if (raisedDiskFrom.value != null && osMinDiskGb.value != null) {
      items.push({
        key: 'mainDiskRaised',
        label: t('billing.page.checkout.autoAdjust.mainDiskRaised', {
          name: osDisplayName.value ?? t('billing.page.checkout.autoAdjust.thisImage'),
          min: osMinDiskGb.value,
          from: baseDiskGb.value + raisedDiskFrom.value,
          to: mainDiskTotalGb.value,
        }),
      })
    }
    return items
  })

  /**
   * Raise the main disk to the OS floor, and REMEMBER that we did.
   *
   * Never lowers. A customer who sized a disk and then switched to an OS with a
   * smaller minimum meant the size they chose; dropping it back would be the
   * same silent edit in the other direction. Only the floor moves.
   */
  watch(
    [selectedTemplateCode, selectedOfferingCode, selectedClientImageId, osTab],
    () => {
      if (mainDiskExtraGb.value < minMainExtraGb.value) {
        raisedDiskFrom.value = mainDiskExtraGb.value
        mainDiskExtraGb.value = minMainExtraGb.value
      }
    }
  )

  /** The customer touching the disk themselves retires our note about it. */
  watch(mainDiskExtraGb, (next, previous) => {
    if (raisedDiskFrom.value != null && next !== previous && next !== minMainExtraGb.value) {
      raisedDiskFrom.value = null
    }
  })

  // ── Add-ons ─────────────────────────────────────────────────────────────
  function isAddonVisible(addon: CatalogAddon) {
    // Hide an IP addon when the selected datacenter has none of that type free.
    if (ipAvailability.value) {
      if (addon.code === 'extra_ipv6' && !ipAvailability.value.ipv6) return false
      if (addon.code === 'extra_ipv4' && !ipAvailability.value.ipv4) return false
    }
    const filter = (addon.os_filter as { os?: string[] } | null)?.os
    if (!filter || filter.length === 0) return true
    if (!selectedTemplate.value) return false
    return filter.includes(selectedTemplate.value.os_family)
  }

  /**
   * Per-addon quantity cap beyond catalog max_quantity (null = none).
   *
   * The IP addon quantity is ADDITIONAL addresses — provisioning allocates the
   * plan's included ones on top — so the plan's own addresses count against the
   * per-instance ceiling and the stepper must stop that much earlier. Reading it
   * as a total let the UI offer 24 extra on a plan that already had one, which
   * the API then refused at the last click.
   */
  function addonIpCap(addon: CatalogAddon): number | null {
    const included =
      addon.code === 'extra_ipv4'
        ? selectedOffering.value?.included_ipv4_per_vm ?? 0
        : addon.code === 'extra_ipv6'
          ? selectedOffering.value?.included_ipv6_per_vm ?? 0
          : null
    if (included == null) return null
    return Math.max(0, MAX_IPS_PER_INSTANCE - included)
  }

  /**
   * Quantity currently selected for a given addon TYPE.
   *
   * By type, not code: codes are operator-editable catalog data, and the
   * licence relationship is a fact about the products, not about their slugs.
   */
  function quantityOfType(type: string): number {
    let total = 0
    for (const addon of billing.catalog?.addons ?? []) {
      if (addon.addon_type === type) total += addonQuantities.value[addon.code] ?? 0
    }
    return total
  }

  /**
   * Client access licences are being bought without the Windows Server licence
   * they attach to.
   *
   * A CAL is the right to have someone sign in to a licensed Windows Server;
   * on its own it is access to a licence the customer does not hold. The
   * catalog cannot express the dependency — `os_filter` only limits CALs to
   * Windows images, which is a different (and weaker) condition — so it lives
   * here, and authoritatively on the order and resize endpoints.
   *
   * True as soon as the licence is absent, whether or not any CAL is selected,
   * so the control can refuse the FIRST one rather than only complain later.
   */
  const needsWindowsLicenceFirst = computed(
    () => quantityOfType('WINDOWS_LICENSE') === 0,
  )

  function incAddon(addon: CatalogAddon) {
    // The dependency is enforced here too, not only in the control: a restored
    // draft or a stale quantity map must not be able to slip past the button.
    if (addon.addon_type === 'CAL_LICENSE' && needsWindowsLicenceFirst.value) return
    const cur = addonQuantities.value[addon.code] ?? 0
    const next = cur + addon.quantity_step
    if (addon.max_quantity != null && next > addon.max_quantity) return
    const ipCap = addonIpCap(addon)
    if (ipCap != null && next > ipCap) return // per-instance IP ceiling
    addonQuantities.value[addon.code] = next
  }
  function decAddon(addon: CatalogAddon) {
    const cur = addonQuantities.value[addon.code] ?? 0
    const next = cur - addon.quantity_step
    addonQuantities.value[addon.code] = next < 0 ? 0 : next
  }
  function toggleBoolAddon(addon: CatalogAddon) {
    addonQuantities.value[addon.code] = addonQuantities.value[addon.code] ? 0 : 1
  }

  /**
   * Dropping the Windows licence drops the CALs with it.
   *
   * Leaving them behind would bill for client access to a licence that is no
   * longer on the order, and would do it silently — the customer switched off
   * one thing and would be charged for another they can no longer use.
   */
  watch(needsWindowsLicenceFirst, (needsIt) => {
    if (!needsIt) return
    for (const addon of billing.catalog?.addons ?? []) {
      if (addon.addon_type === 'CAL_LICENSE' && addonQuantities.value[addon.code]) {
        addonQuantities.value[addon.code] = 0
      }
    }
  })
  /** True for addons the customer toggles instead of counting. */
  function isToggleAddon(addon: CatalogAddon) {
    return !addon.quantity_enabled || addon.quantity_follows_vcpu
  }
  function rateForAddon(addon: CatalogAddon) {
    return billing.formatMicroCents(addon.rates_by_term[checkoutTerm.value])
  }
  /** Hourly rate a per-vCPU addon costs on the selected plan (rate × vCPU). */
  function vcpuAddonTotalRate(addon: CatalogAddon) {
    const rate = BigInt(addon.rates_by_term[checkoutTerm.value] ?? '0')
    const vcpu = BigInt(selectedOffering.value?.vcpu ?? 0)
    return billing.formatMicroCents((rate * vcpu).toString())
  }
  /**
   * Quantity to SEND for an addon. `quantity_follows_vcpu` addons (Windows
   * license) are priced per vCPU and the UI keeps them as a simple on/off, so the
   * quantity is the selected plan's vCPU count. The server derives the same value
   * — sending it keeps the local line-item display honest.
   */
  function payloadAddonQuantity(addon: CatalogAddon, quantity: number): number {
    if (!addon.quantity_follows_vcpu) return quantity
    return selectedOffering.value?.vcpu ?? quantity
  }
  function addonPayload(): { addon_code: string; quantity: number }[] {
    const byCode = new Map((billing.catalog?.addons ?? []).map((a) => [a.code, a]))
    return Object.entries(addonQuantities.value)
      .filter(([, qty]) => qty > 0)
      .map(([addon_code, quantity]) => {
        const addon = byCode.get(addon_code)
        return {
          addon_code,
          quantity: addon ? payloadAddonQuantity(addon, quantity) : quantity,
        }
      })
  }

  // ── Plan row pricing ────────────────────────────────────────────────────
  /**
   * Plans are quoted per MONTH, not per cycle. The cycle total is the right
   * number on the invoice and the wrong one in a comparison table: it made an
   * annual commitment — the cheapest option per month — display the biggest
   * figure on the page, so committing looked like paying more.
   *
   * The struck-through reference is the same plan with no commitment, which is
   * what the discount is actually against.
   */
  function planMonthlyPrice(offering: CatalogOffering): string {
    return pricing.fmt(pricing.perMonth(offering.rates_by_term[checkoutTerm.value]))
  }
  function planHourlyPrice(offering: CatalogOffering): string {
    return billing.formatMicroCents(offering.rates_by_term[checkoutTerm.value])
  }
  function planReferencePrice(offering: CatalogOffering): string | null {
    if (!pricing.savingsVsOnDemand(offering.rates_by_term, checkoutTerm.value)) {
      return null
    }
    return pricing.fmt(pricing.perMonth(offering.rates_by_term.ON_DEMAND))
  }
  function planSavingsPct(offering: CatalogOffering): number | null {
    return (
      pricing.savingsVsOnDemand(offering.rates_by_term, checkoutTerm.value)?.pct ??
      null
    )
  }

  // ── Live simulation ─────────────────────────────────────────────────────
  const simulating = ref(false)
  const simulationError = ref<string | null>(null)
  const quote = computed(() => billing.lastQuote)

  /**
   * The same configuration priced with no commitment (µ¢/month) — the baseline
   * every savings figure on the page is measured against. Quoted by the server
   * rather than derived from the plan rate alone: disks and add-ons have their
   * own on-demand multipliers, so a plan-only estimate would understate what
   * committing actually saves.
   *
   * It rides along on the quote (`compare_term`) instead of being a second
   * request. This endpoint is public now, so its rate is bounded by how many
   * browsers point at us, and it was firing twice per slider pixel.
   */
  const onDemandMonthlyMicroCents = computed(() => {
    const baseline =
      checkoutTerm.value === 'ON_DEMAND'
        ? quote.value
        : quote.value?.comparison ?? null
    const monthly = pricing.perMonth(baseline?.total_hourly_rate_micro_cents)
    return monthly?.toString() ?? null
  })

  /**
   * One builder for both the displayed quote and the placed order — they used to
   * be assembled separately and disagreed about `vpc_id`, so the price on screen
   * was not always the price being ordered.
   */
  function buildQuoteInput(term: BillingTermKind = checkoutTerm.value): SimulationInput {
    return {
      offering_code: selectedOfferingCode.value!,
      term,
      os_choice: osChoice.value!,
      main_disk_extra_gb: mainDiskExtraGb.value || undefined,
      extra_disks: extraDisks.value.filter((d) => d.size_gb > 0),
      addons: addonPayload(),
      datacenter_id: selectedDatacenterId.value!,
      connectivity_mode: connectivityMode.value,
      vpc_id: selectedVpcId.value ?? undefined,
    }
  }

  const canQuote = computed(
    () =>
      !!selectedOfferingCode.value && !!osChoice.value && !!selectedDatacenterId.value
  )

  let quoteSeq = 0
  let quoteTimer: ReturnType<typeof setTimeout> | null = null

  async function refreshQuote() {
    if (!canQuote.value) return
    simulating.value = true
    simulationError.value = null
    const seq = ++quoteSeq
    try {
      await billing.simulate({
        ...buildQuoteInput(),
        // On-demand IS the baseline, so asking it to compare against itself
        // would be a wasted half of the response.
        ...(checkoutTerm.value === 'ON_DEMAND'
          ? {}
          : { compare_term: 'ON_DEMAND' as BillingTermKind }),
      })
    } catch (err: any) {
      if (seq === quoteSeq) {
        simulationError.value = err?.response?.data?.message ?? err.message
      }
    } finally {
      if (seq === quoteSeq) simulating.value = false
    }
  }

  /**
   * Disk sliders emit continuously, so an un-debounced watcher would fire a
   * simulation per pixel dragged. Coalesce, and stamp each run so a slow early
   * response can never overwrite a newer one.
   */
  function scheduleQuoteRefresh() {
    if (quoteTimer) clearTimeout(quoteTimer)
    quoteTimer = setTimeout(refreshQuote, 250)
  }
  onUnmounted(() => {
    if (quoteTimer) clearTimeout(quoteTimer)
  })

  watch(
    [
      selectedOfferingCode,
      checkoutTerm,
      osTab,
      selectedTemplateCode,
      // `selectedClientImageId` was missing here, so switching between two of
      // your own images — or from an image to a blank instance — left the price
      // of the PREVIOUS one on screen. `osChoice` reads it; the watcher has to.
      selectedClientImageId,
      mainDiskExtraGb,
      extraDisks,
      addonQuantities,
      selectedDatacenterId,
      connectivityMode,
      selectedVpcId,
    ],
    () => {
      if (canQuote.value) scheduleQuoteRefresh()
    },
    { deep: true }
  )

  // ── What is still missing ───────────────────────────────────────────────
  /**
   * The CONFIGURATION half of the order blockers — everything a page can know
   * without knowing who is signed in. Each page appends its own (a subscription
   * to pick, a password to set, an account to create) and derives its CTA state
   * from the combined list, so the button and the checklist cannot disagree.
   */
  const configurationBlockers = computed<{ key: string; label: string }[]>(() => {
    const blockers: { key: string; label: string }[] = []
    if (datacenters.value.length > 0 && deployableDatacenters.value.length === 0)
      blockers.push({
        key: 'noCapacity',
        label: t('billing.page.checkout.noDeployableDatacenters'),
      })
    else if (!selectedDatacenterId.value)
      blockers.push({
        key: 'datacenter',
        label: t('billing.page.checkout.blockers.datacenter'),
      })
    if (!selectedOfferingCode.value)
      blockers.push({ key: 'plan', label: t('billing.page.checkout.blockers.plan') })
    if (!osChoice.value)
      blockers.push({ key: 'os', label: t('billing.page.checkout.blockers.os') })
    if (connectivityMode.value === 'private' && !selectedVpcId.value)
      blockers.push({ key: 'vpc', label: t('billing.page.checkout.blockers.vpc') })
    if (osRequirementsUnmet.value)
      blockers.push({
        key: 'osMin',
        label: t('billing.page.checkout.blockers.osMin', {
          name: osDisplayName.value ?? '',
        }),
      })
    if (quote.value && quote.value.total_hourly_rate_micro_cents === '0')
      blockers.push({
        key: 'zeroRate',
        label: t('billing.page.checkout.blockers.zeroRate'),
      })
    if (quantityOfType('CAL_LICENSE') > 0 && needsWindowsLicenceFirst.value)
      blockers.push({
        key: 'calWithoutWindowsLicence',
        label: t('billing.page.checkout.blockers.calWithoutWindowsLicence'),
      })
    return blockers
  })

  // ── Step completion ─────────────────────────────────────────────────────
  const datacenterDone = computed(() => !!selectedDatacenterId.value)
  /**
   * The image is chosen BEFORE the plan, so a plan too small for it is the
   * PLAN's problem: that is the step the customer has to change, and the one
   * that carries the warning. The image step is done once something is chosen.
   */
  const planDone = computed(() => !!selectedOffering.value && !planBelowOsMin.value)
  const osDone = computed(() => !!osChoice.value)
  const storageDone = computed(() => !diskBelowOsMin.value)
  const connectivityDone = computed(
    () => connectivityMode.value === 'public' || !!selectedVpcId.value
  )
  const hasVisibleAddons = computed(
    () => billing.catalog?.addons.some(isAddonVisible) ?? false
  )

  return {
    // state
    selectedDatacenterId,
    selectedOfferingCode,
    selectedTerm,
    osTab,
    selectedClientImageId,
    selectedOsTemplateCode,
    selectedMarketplaceTemplateCode,
    selectedTemplateCode,
    connectivityMode,
    selectedVpcId,
    mainDiskExtraGb,
    extraDisks,
    addonQuantities,
    checkoutTerm,
    // locations
    deployableDatacenters,
    selectedDatacenter,
    ipAvailability,
    vpcsInDatacenter,
    selectedVpcName,
    // plans + storage
    computeOfferings,
    selectedOffering,
    baseDiskGb,
    diskStep,
    minExtraDiskGb,
    mainDiskTotalGb,
    totalStorageGb,
    // OS
    selectedTemplate,
    selectedClientImage,
    osChoice,
    osDisplayName,
    osMinDiskGb,
    osMinRamMb,
    osMinVcpu,
    minMainExtraGb,
    diskBelowOsMin,
    ramBelowOsMin,
    cpuBelowOsMin,
    planBelowOsMin,
    osRequirementsUnmet,
    offeringMeetsOsMin,
    autoAdjustments,
    // addons
    isAddonVisible,
    addonIpCap,
    needsWindowsLicenceFirst,
    isToggleAddon,
    incAddon,
    decAddon,
    toggleBoolAddon,
    rateForAddon,
    vcpuAddonTotalRate,
    addonPayload,
    hasVisibleAddons,
    // plan pricing
    planMonthlyPrice,
    planHourlyPrice,
    planReferencePrice,
    planSavingsPct,
    // quote
    quote,
    simulating,
    simulationError,
    onDemandMonthlyMicroCents,
    buildQuoteInput,
    refreshQuote,
    scheduleQuoteRefresh,
    canQuote,
    // status
    configurationBlockers,
    datacenterDone,
    planDone,
    osDone,
    storageDone,
    connectivityDone,
  }
}

export type CheckoutConfigurator = ReturnType<typeof useCheckoutConfigurator>
